import { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../data/translations';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { 
  Calendar, ArrowRight, Activity, User, Waves, 
  Dumbbell, Zap, Heart, Shield, CheckCircle, 
  GraduationCap, MapPin, Phone, Mail, Clock, 
  MessageCircle, Send, AlertCircle
} from 'lucide-react';
// Shield is kept for services icon
import axios from 'axios';
import miguelAndradeBg from '../assets/Miguel-Andrade-Fisioterapeuta.webp';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const { t, language } = useLanguage();
  const whatsappNumber = '351911739498';
  const whatsappUrl = `https://wa.me/${whatsappNumber}`;
  const calendlyUrl = 'https://calendly.com/diogodapalma/30min';

  // Contact form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      await axios.post(`${API}/contact`, formData);
      setSubmitStatus('success');
      setFormData({ name: '', email: '', phone: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Load Calendly script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        existingScript.remove();
      }
    };
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  // Data
  const features = [
    { icon: User, title: t(translations.features.individual.title), desc: t(translations.features.individual.desc) },
    { icon: Activity, title: t(translations.features.recovery.title), desc: t(translations.features.recovery.desc) },
    { icon: Waves, title: t(translations.features.surf.title), desc: t(translations.features.surf.desc) },
  ];

  const services = [
    { key: 'musculoskeletal', icon: Activity, ...translations.services.musculoskeletal, image: 'https://images.pexels.com/photos/4506072/pexels-photo-4506072.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { key: 'rehabilitation', icon: Dumbbell, ...translations.services.rehabilitation, image: 'https://images.pexels.com/photos/20860586/pexels-photo-20860586.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { key: 'backPain', icon: Zap, ...translations.services.backPain, image: 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?auto=format&fit=crop&w=800&q=80' },
    { key: 'surfInjury', icon: Waves, ...translations.services.surfInjury, image: 'https://images.unsplash.com/photo-1502680390469-be75c86b636f?auto=format&fit=crop&w=800&q=80' },
    { key: 'personalized', icon: Heart, ...translations.services.personalized, image: 'https://images.pexels.com/photos/4506072/pexels-photo-4506072.jpeg?auto=compress&cs=tinysrgb&w=800' },
    { key: 'prevention', icon: Shield, ...translations.services.prevention, image: 'https://images.pexels.com/photos/20860586/pexels-photo-20860586.jpeg?auto=compress&cs=tinysrgb&w=800' },
  ];

  const contactInfo = [
    { icon: MapPin, title: t(translations.contact.address), content: 'Edifício Atlântico, Rua Dr. Eduardo Burnay, Sala 12\n2655-368 Ericeira, Portugal', link: 'https://maps.google.com/?q=Rua+Dr.+Eduardo+Burnay,+Ericeira,+Portugal' },
    { icon: Phone, title: t(translations.contact.phone), content: '+351 911 739 498', link: 'tel:+351911739498' },
    { icon: Mail, title: 'Email', content: 'info@diogoh.pt', link: 'mailto:info@diogoh.pt' },
  ];

  return (
    <div data-testid="landing-page">
      {/* ========== HERO SECTION ========== */}
      <section 
        id="home"
        data-testid="hero-section"
        className="relative min-h-screen flex items-center pt-20 overflow-hidden"
      >
        <div className="absolute inset-0 z-0">
          <img
            src="https://images.unsplash.com/photo-1502680390469-be75c86b636f?auto=format&fit=crop&w=2000&q=80"
            alt="Ericeira surf"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-deep/95 via-primary-deep/80 to-primary-deep/60" />
        </div>

        <div className="relative z-10 container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="max-w-2xl">
            <span className="inline-block px-4 py-2 bg-white/10 backdrop-blur-sm rounded-full text-mist-blue text-sm font-medium mb-6 animate-fade-in">
              Ericeira, Portugal
            </span>
            
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 animate-fade-in-up">
              {t(translations.hero.title)}
            </h1>
            
            <p className="text-lg md:text-xl text-white/90 leading-relaxed mb-10 animate-fade-in-up animation-delay-100 drop-shadow-md">
              {t(translations.hero.subtitle)}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 animate-fade-in-up animation-delay-200">
              <Button
                onClick={() => scrollToSection('appointments')}
                data-testid="hero-cta-primary"
                className="h-14 px-8 rounded-full bg-white text-primary-deep hover:bg-mist-blue transition-all duration-300 shadow-lg hover:shadow-xl text-base font-semibold"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {t(translations.hero.cta)}
              </Button>
              
              <Button
                onClick={() => scrollToSection('services')}
                data-testid="hero-cta-secondary"
                variant="outline"
                className="h-14 px-8 rounded-full bg-transparent border-2 border-white/30 text-white hover:bg-white/10 hover:border-white/50 transition-all duration-300 text-base"
              >
                {t(translations.hero.ctaSecondary)}
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>

        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-white/50 rounded-full mt-2 animate-scroll" />
          </div>
        </div>
      </section>

      {/* ========== FEATURES SECTION ========== */}
      <section data-testid="features-section" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-foreground-dark mb-4">
              {t(translations.features.title)}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                data-testid={`feature-card-${index}`}
                className="group relative overflow-hidden rounded-2xl bg-background-light p-8 transition-all duration-300 hover:shadow-lg hover:bg-mist-blue/10"
              >
                <div className="w-14 h-14 rounded-xl bg-primary-deep/10 flex items-center justify-center mb-6 group-hover:bg-primary-deep group-hover:text-white transition-all duration-300">
                  <feature.icon className="w-7 h-7 text-primary-deep group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-foreground-dark mb-3">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== SERVICES SECTION ========== */}
      <section id="services" data-testid="services-section" className="py-20 md:py-32 bg-background-light">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-dark mb-4">
              {t(translations.services.title)}
            </h2>
            <p className="text-lg text-slate-600">{t(translations.services.subtitle)}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service) => (
              <div
                key={service.key}
                data-testid={`service-card-${service.key}`}
                className="group relative overflow-hidden rounded-2xl bg-white shadow-sm hover:shadow-lg transition-all duration-300 border border-slate-100"
              >
                <div className="relative h-48 overflow-hidden">
                  <img
                    src={service.image}
                    alt={t(service.title)}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <div className="w-12 h-12 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-white" />
                    </div>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="font-heading text-xl font-semibold text-foreground-dark mb-3 group-hover:text-primary-deep transition-colors duration-200">
                    {t(service.title)}
                  </h3>
                  <p className="text-slate-600 leading-relaxed">{t(service.desc)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ========== ABOUT SECTION ========== */}
      <section id="about" data-testid="about-section" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Image */}
            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src={miguelAndradeBg}
                  className="w-full aspect-[4/5] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary-deep/20 to-transparent" />
              </div>
              
              <div className="absolute -bottom-6 -right-6 md:right-6 bg-white rounded-2xl p-6 shadow-xl max-w-xs">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-full bg-primary-deep/10 flex items-center justify-center">
                    <GraduationCap className="w-5 h-5 text-primary-deep" />
                  </div>
                  <span className="font-heading font-semibold text-foreground-dark">
                    {t({ pt: 'Fisioterapeuta Licenciado', en: 'Licensed Physiotherapist' })}
                  </span>
                </div>
                <p className="text-sm text-slate-600">
                  {t({ pt: 'Especialista em reabilitação física e lesões desportivas', en: 'Specialist in physical rehabilitation and sports injuries' })}
                </p>
              </div>
            </div>

            {/* Content */}
            <div>
              <span className="inline-block px-4 py-2 bg-primary-deep/10 rounded-full text-primary-deep text-sm font-medium mb-6">
                {t(translations.about.subtitle)}
              </span>
              
              <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-dark leading-tight mb-6">
                Miguel Andrade
              </h2>
              
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t(translations.about.intro)}
              </p>

              {/* Philosophy & Experience */}
              <div className="space-y-6 mb-8">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-primary-deep/10 flex items-center justify-center flex-shrink-0">
                    <Heart className="w-5 h-5 text-primary-deep" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-dark mb-1">
                      {t(translations.about.philosophy.title)}
                    </h3>
                    <p className="text-slate-600 text-sm">{t(translations.about.philosophy.text)}</p>
                  </div>
                </div>
              </div>

              <ul className="space-y-3 mb-8">
                {t(translations.about.experience.items).map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <CheckCircle className="w-5 h-5 text-primary-deep flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{item}</span>
                  </li>
                ))}
              </ul>

              <Button
                onClick={() => scrollToSection('appointments')}
                data-testid="about-cta-button"
                className="h-12 px-8 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-all duration-300 shadow-md hover:shadow-lg"
              >
                <Calendar className="w-5 h-5 mr-2" />
                {t(translations.hero.cta)}
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* ========== APPOINTMENTS SECTION ========== */}
      <section id="appointments" data-testid="appointments-section" className="py-20 md:py-32 bg-background-light">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-dark mb-4">
              {t(translations.appointments.title)}
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              {t(translations.appointments.intro)}
            </p>
          </div>

          {/* Calendly Embed */}
          <div className="max-w-4xl mx-auto">
            <div className="rounded-2xl overflow-hidden shadow-lg border border-slate-200 bg-white">
              <div
                className="calendly-inline-widget"
                data-url={`${calendlyUrl}?hide_gdpr_banner=1&primary_color=0f4c75`}
                style={{ minWidth: '320px', height: '700px' }}
                data-testid="calendly-widget"
              />
            </div>

            <div className="mt-12 text-center">
              <p className="text-slate-600 mb-6">{t(translations.appointments.alternative)}</p>
              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="appointments-whatsapp-button">
                <Button className="h-12 px-8 rounded-full bg-green-500 text-white hover:bg-green-600 transition-all duration-300 shadow-md hover:shadow-lg">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {t(translations.appointments.whatsapp)}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ========== CONTACT SECTION ========== */}
      <section id="contact" data-testid="contact-section" className="py-20 md:py-32 bg-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-dark mb-4">
              {t(translations.contact.title)}
            </h2>
            <p className="text-lg text-slate-600">{t(translations.contact.subtitle)}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Contact Info */}
            <div className="space-y-6">
              {contactInfo.map((item, index) => (
                <a
                  key={index}
                  href={item.link}
                  target={item.link.startsWith('http') ? '_blank' : undefined}
                  rel={item.link.startsWith('http') ? 'noopener noreferrer' : undefined}
                  data-testid={`contact-info-${index}`}
                  className="flex items-start gap-4 p-6 rounded-2xl bg-background-light hover:bg-mist-blue/20 transition-colors duration-300"
                >
                  <div className="w-12 h-12 rounded-xl bg-primary-deep/10 flex items-center justify-center flex-shrink-0">
                    <item.icon className="w-6 h-6 text-primary-deep" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-dark mb-1">{item.title}</h3>
                    <p className="text-slate-600 whitespace-pre-line">{item.content}</p>
                  </div>
                </a>
              ))}

              {/* Hours */}
              <div className="p-6 rounded-2xl bg-background-light">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 rounded-xl bg-primary-deep/10 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-primary-deep" />
                  </div>
                  <div>
                    <h3 className="font-heading font-semibold text-foreground-dark mb-2">{t(translations.contact.hours)}</h3>
                    <div className="space-y-1 text-slate-600">
                      {t(translations.contact.hoursDetail).map((line, index) => (
                        <p key={index}>{line}</p>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-button">
                <Button className="w-full h-14 rounded-full bg-green-500 text-white hover:bg-green-600 transition-all duration-300 shadow-md hover:shadow-lg text-base font-medium">
                  <MessageCircle className="w-5 h-5 mr-2" />
                  {t(translations.appointments.whatsapp)}
                </Button>
              </a>
            </div>

            {/* Contact Form */}
            <div className="bg-background-light rounded-2xl p-8 md:p-10">
              <h3 className="font-heading text-2xl font-bold text-foreground-dark mb-6">
                {t(translations.contact.form.title)}
              </h3>

              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div className="space-y-2">
                  <Label htmlFor="name">{t(translations.contact.form.name)}</Label>
                  <Input id="name" name="name" value={formData.name} onChange={handleChange} required data-testid="contact-form-name" className="h-12 rounded-lg border-slate-200 focus:border-primary-deep focus:ring-primary-deep" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">{t(translations.contact.form.email)}</Label>
                  <Input id="email" name="email" type="email" value={formData.email} onChange={handleChange} required data-testid="contact-form-email" className="h-12 rounded-lg border-slate-200 focus:border-primary-deep focus:ring-primary-deep" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">{t(translations.contact.form.phone)}</Label>
                  <Input id="phone" name="phone" type="tel" value={formData.phone} onChange={handleChange} data-testid="contact-form-phone" className="h-12 rounded-lg border-slate-200 focus:border-primary-deep focus:ring-primary-deep" />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="message">{t(translations.contact.form.message)}</Label>
                  <Textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} data-testid="contact-form-message" className="rounded-lg border-slate-200 focus:border-primary-deep focus:ring-primary-deep resize-none" />
                </div>

                {submitStatus === 'success' && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-green-50 text-green-700" data-testid="contact-form-success">
                    <CheckCircle className="w-5 h-5" />
                    <span>{t(translations.contact.form.success)}</span>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="flex items-center gap-2 p-4 rounded-lg bg-red-50 text-red-700" data-testid="contact-form-error">
                    <AlertCircle className="w-5 h-5" />
                    <span>{t(translations.contact.form.error)}</span>
                  </div>
                )}

                <Button type="submit" disabled={isSubmitting} data-testid="contact-form-submit" className="w-full h-14 rounded-full bg-primary-deep text-white hover:bg-accent-blue transition-all duration-300 shadow-md hover:shadow-lg text-base font-medium disabled:opacity-50">
                  {isSubmitting ? (
                    <span className="flex items-center gap-2">
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      {t({ pt: 'A enviar...', en: 'Sending...' })}
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      <Send className="w-5 h-5" />
                      {t(translations.contact.form.submit)}
                    </span>
                  )}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* ========== MAP SECTION ========== */}
      <section data-testid="map-section" className="py-20 md:py-28 bg-background-light">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl">
          <div className="rounded-2xl overflow-hidden shadow-lg">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3102.8912343!2d-9.4164!3d38.9631!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0xd1ecf8d5a8d8d8d%3A0x8d8d8d8d8d8d8d8d!2sRua%20Dr.%20Eduardo%20Burnay%2C%20Ericeira%2C%20Portugal!5e0!3m2!1sen!2spt!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Localização da clínica"
              data-testid="google-map"
            />
          </div>
        </div>
      </section>

      {/* ========== CTA SECTION ========== */}
      <section data-testid="cta-section" className="py-20 md:py-32 bg-primary-deep text-white">
        <div className="container mx-auto px-6 md:px-12 lg:px-24 max-w-7xl text-center">
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {t(translations.hero.title)}
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-10">
            {t(translations.hero.subtitle)}
          </p>
          <Button
            onClick={() => scrollToSection('appointments')}
            data-testid="cta-section-button"
            className="h-14 px-10 rounded-full bg-white text-primary-deep hover:bg-mist-blue transition-all duration-300 shadow-lg hover:shadow-xl text-base font-semibold"
          >
            <Calendar className="w-5 h-5 mr-2" />
            {t(translations.hero.cta)}
          </Button>
        </div>
      </section>
    </div>
  );
}
